import React, { useEffect, useState } from 'react';
import { Radio, Button, Spin, Space, Input, App } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import { decodeToken } from "react-jwt";
import './Mfa.css';
import logo from '../../public/images/logo.svg';

// GraphQL queries and mutations
export const GET_USER_MFA_DATA = gql`
  query GetUserMfaData($userId: Float!) {
    getUserMfaData(id: $userId) {
      email
      phoneNumbersList {
        id
        number
        type
      }
    }
  }
`;

const SEND_CONFIRMATION_TOKEN = gql`
  mutation SendConfirmationToken($username: String!, $type: String!, $phoneNumberId: Float) {
    sendConfirmationToken(username: $username, type: $type, phoneNumberId: $phoneNumberId) {
      username
      tokenSent
    }
  }
`;

const VERIFY_CONFIRMATION_TOKEN = gql`
  mutation VerifyConfirmationToken($username: String!, $token: String!) {
    verifyConfirmationToken(username: $username, token: $token) {
      accessToken
      refreshToken
    }
  }
`;

const Mfa: React.FC = () => {
  const { message } = App.useApp();
  const [selectedMethod, setSelectedMethod] = useState('');
  const [tokenSent, setTokenSent] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  // Decode the JWT token to get userId and username
  const token = localStorage.getItem('accessToken');
  const result = decodeToken(token as string) as any;
  
  // For contacts, use clientId; for users, use id
  const id = result?.userType === 'contact' ? result?.clientId : result?.id;
  const username = result?.username || result?.email; // Fallback to email if username is null

  const { data, loading, error } = useQuery(GET_USER_MFA_DATA, {
    variables: { userId: id },
  });

  const [sendConfirmationToken, { loading: tokenLoading }] = useMutation(SEND_CONFIRMATION_TOKEN);
  const [verifyConfirmationToken, { loading: verifyLoading }] = useMutation(VERIFY_CONFIRMATION_TOKEN);
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      message.error('Failed to load MFA data');
    }
  }, [error, message]);

  const handleSendToken = async () => {
    const [methodType, methodValue] = selectedMethod.split('-');
    try {
      const variables: any = {
        username: result?.email || result?.username, // Use email from token for contacts
        type: methodType,
      };

      // If phone method is selected, include phoneNumberId
      if (methodType === 'phone') {
        // Extract phone number ID from the selected phone option
        const selectedPhone = data.getUserMfaData.phoneNumbersList.find(
          (phone: any) => phone.number === methodValue
        );
        if (selectedPhone) {
          variables.phoneNumberId = selectedPhone.id;
        }
      }

      const response = await sendConfirmationToken({ variables });
      
      if (response.data.sendConfirmationToken.tokenSent) {
        setTokenSent(true);
        message.success('MFA token sent successfully');
      } else {
        message.error('Failed to send MFA token');
      }
    } catch (e) {
      message.error('Failed to send MFA token');
    }
  };

  const handleContinue = () => {
    if (!selectedMethod) {
      message.error('Please select an MFA method');
      return;
    }
    handleSendToken();
  };

  const handleVerify = async () => {
    try {
      const response = await verifyConfirmationToken({
        variables: {
          username: result?.email || result?.username, // Use email from token for contacts
          token: mfaCode,
        },
      });
      const { accessToken, refreshToken } =
        response.data.verifyConfirmationToken;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      const decoded: any = decodeToken(accessToken);
      const role = decoded?.role;
      const userId = decoded?.userType === 'contact' ? decoded?.clientId : decoded?.id;
      localStorage.setItem('userId', userId);

      message.success('MFA verified successfully');

      if (role === 'admin' || role === 'super_admin') {
        navigate('/statements');
      } else {
        navigate('/accounts');
      }
    } catch (e) {
      message.error('Failed to verify MFA code');
    }
  };

  if (loading || tokenLoading || verifyLoading) {
    return (
      <div className="mfa-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="mfa-container">
      <img src={logo} alt="Paytrust logo" className="mfa-logo" />
      <div className="mfa-content">
        {!tokenSent ? (
          <>
            <h2 style={{ margin: 0 }}>Verify Your Identity</h2>
            <p>Select the method through which you'd like to verify your account</p>
            <Radio.Group onChange={(e) => setSelectedMethod(e.target.value)}>
              <Space direction="vertical">
                <Radio value={`email-${data.getUserMfaData.email}`}>
                  {data.getUserMfaData.email}
                </Radio>
                {data.getUserMfaData.phoneNumbersList.map((phone: any) => (
                  <Radio key={phone.id} value={`phone-${phone.number}`}>
                    {phone.number} ({phone.type})
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            <Button type="primary" onClick={handleContinue} disabled={!selectedMethod}>
              Continue
            </Button>
          </>
        ) : (
          <>
            <h2 style={{ margin: 0 }}>MFA Code</h2>
            <p>Enter your MFA Code. The code is valid for 5 minutes</p>
            <Input
              placeholder="Enter MFA code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
            />
            <Button type="primary" onClick={handleVerify} disabled={!mfaCode}>
              Verify
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default Mfa;